import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Inbox, X, Plus, ExternalLink, RefreshCw, Loader2, CalendarDays, Upload, Undo2 } from "lucide-react";

/**
 * IcalInboxTab (iter185 – 02/2026)
 * ------------------------------------------------------------------------
 * Nuova gestione delle prenotazioni Booking.com importate via iCal.
 * NON entrano più nel calendario prenotazioni: restano qui finché l'host
 * decide se importare il CSV Booking (dati completi) oppure crearla a mano.
 *
 * Azioni per ogni riga:
 *   • Depenna  → rimuove dalla lista (reversibile)
 *   • Crea prenotazione manuale → apre il form pre-riempito con
 *     camera + date; l'host aggiunge nome/telefono/prezzo mancanti
 *   • Vai su Booking → apre extranet Booking.com in nuova tab
 */
export default function IcalInboxTab({ onCreateBookingFromIcal }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUid, setBusyUid] = useState(null);
  // iter208 — URL Booking.com personalizzato dall'host (menu Profilo → Account OTA)
  const [bookingUrl, setBookingUrl] = useState("https://admin.booking.com/hotel/hoteladmin/extranet_ng/manage/booking.html");
  useEffect(() => {
    // Carica lazy — se manca l'endpoint teniamo il default statico
    api.get("/admin/ota-accounts")
      .then((r) => { if (r.data?.booking_url) setBookingUrl(r.data.booking_url); })
      .catch(() => {});
  }, []);
  // iter201 — Deep-link: se l'URL contiene ?uid=..., scrolliamo + evidenziamo
  // l'entry corrispondente non appena la lista è caricata. Usiamo un ref per
  // ricordare l'UID target anche se le query change.
  const [searchParams] = useSearchParams();
  const highlightUid = searchParams.get("uid");
  const [highlightedUid, setHighlightedUid] = useState(null);
  const rowRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ical-inbox");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento inbox iCal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter201 — Quando la lista è pronta E c'è un UID target in URL, scrolla e
  // "flash" per 3 secondi l'entry giusta. Se l'UID non è in inbox (già
  // trasformato o depennato) mostriamo un toast informativo.
  useEffect(() => {
    if (loading || !highlightUid || items.length === 0) return;
    const target = items.find((it) => it.uid === highlightUid);
    if (!target) {
      toast.info("Questa prenotazione iCal non è più in inbox (già trasformata o depennata)");
      return;
    }
    setHighlightedUid(highlightUid);
    // Scroll dopo un microtick per dare al DOM il tempo di rendere le righe
    setTimeout(() => {
      const el = rowRefs.current[highlightUid];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
    const t = setTimeout(() => setHighlightedUid(null), 3200);
    return () => clearTimeout(t);
  }, [loading, items, highlightUid]);

  const dismiss = async (uid) => {
    if (!window.confirm("Depennare questa notifica? La camera resterà occupata sul calendario ma non riceverai più avvisi.")) return;
    setBusyUid(uid);
    try {
      await api.post("/admin/ical-inbox/dismiss", { uid });
      toast.success("Notifica depennata");
      setItems((prev) => prev.filter((it) => it.uid !== uid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusyUid(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  // iter228 — Formato relativo compatto per il timestamp "arrivata".
  // Es. "5 min fa", "2h fa", "ieri", "3g fa", "18 ago" per notifiche
  // più vecchie di una settimana.
  const fmtRelativeShort = (iso) => {
    if (!iso) return "";
    try {
      const now = new Date();
      const dt = new Date(iso);
      const diffMs = now - dt;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "adesso";
      if (diffMin < 60) return `${diffMin} min fa`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `${diffH}h fa`;
      const diffD = Math.floor(diffH / 24);
      if (diffD === 1) return "ieri";
      if (diffD < 7) return `${diffD}g fa`;
      return dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    } catch {
      return "";
    }
  };

  // iter209/iter210/iter211 — Upload XLS/CSV con anteprima per riga.
  //   1. selezione file → modal "come gestire le non abbinate" (only_merge)
  //   2. dry_run → modal preview con lista match (checkbox per riga)
  //   3. apply con approved_codes = solo quelli spuntati
  const [uploadingXls, setUploadingXls] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingMode, setPendingMode] = useState(null); // null|true|false (only_merge)
  const [preview, setPreview] = useState(null); // {matches:[], selected:Set}
  const askUploadMode = (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    setPendingFile(file);
  };
  const doDryRun = async (onlyMerge) => {
    if (!pendingFile) return;
    setPendingMode(onlyMerge);
    setUploadingXls(true);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      const res = await api.post(
        `/admin/bookings/import-booking-xls?only_merge=${onlyMerge ? "true" : "false"}&dry_run=true`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const matches = res.data?.preview_matches || [];
      if (matches.length === 0) {
        toast.info("Nessuna riga leggibile nel file");
        setPendingFile(null);
        return;
      }
      // Default: tutto selezionato
      const selected = new Set(matches.map((m) => m.code));
      setPreview({ matches, selected });
    } catch (e) {
      toast.error("Errore durante l'anteprima — controlla il formato del file");
    } finally {
      setUploadingXls(false);
    }
  };
  const applyApproved = async () => {
    if (!pendingFile || !preview) return;
    const codes = Array.from(preview.selected);
    if (codes.length === 0) {
      toast.info("Non hai selezionato nessuna riga da applicare");
      return;
    }
    setUploadingXls(true);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      const url = `/admin/bookings/import-booking-xls?only_merge=${pendingMode ? "true" : "false"}&approved_codes=${encodeURIComponent(codes.join(","))}`;
      const res = await api.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data || {};
      toast.success(`Applicato · ${d.updated || 0} aggiornate · ${d.created || 0} create · ${d.skipped || 0} skippate`, { duration: 8000 });
      setPreview(null);
      setPendingFile(null);
      setPendingMode(null);
      await load();
    } catch (e) {
      toast.error("Errore durante l'applicazione");
    } finally {
      setUploadingXls(false);
    }
  };

  // iter212 — Annulla ultimo import CSV
  const [undoPreview, setUndoPreview] = useState(null); // {imported_at, will_delete:[], will_keep_updated:[], will_unmerge_externals:number}
  const [undoLoading, setUndoLoading] = useState(false);
  const openUndoPreview = async () => {
    setUndoLoading(true);
    try {
      const { data } = await api.get("/admin/bookings/last-csv-import-preview");
      if (!data?.found) {
        toast.info("Nessun import CSV recente da annullare.");
        return;
      }
      if ((data.will_delete?.length || 0) === 0 && (data.will_unmerge_externals || 0) === 0) {
        toast.info("L'ultimo import ha solo aggiornato prenotazioni esistenti — niente da cancellare.");
        return;
      }
      setUndoPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima annullamento");
    } finally {
      setUndoLoading(false);
    }
  };
  const confirmUndo = async () => {
    setUndoLoading(true);
    try {
      const { data } = await api.post("/admin/bookings/undo-last-csv-import");
      toast.success(`Import annullato · ${data.deleted || 0} prenotazioni cancellate · ${data.unmerged_externals || 0} iCal ripristinati`, { duration: 8000 });
      setUndoPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'annullamento");
    } finally {
      setUndoLoading(false);
    }
  };

  const fmtDateShort = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-4" data-testid="ical-inbox-tab">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4">
        {/* iter230 — Header responsive.
            Mobile: titolo su una riga, pulsanti stacked full-width.
            Desktop: tutto sulla stessa riga. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#AC5D45]" />
            <h2 className="font-display text-xl text-[#1E3A5F]">iCal in arrivo</h2>
            {items.length > 0 && (
              <span className="ml-1 bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5" data-testid="ical-inbox-count">
                {items.length}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 sm:flex sm:flex-row gap-2">
            <Button onClick={load} variant="outline" size="sm" data-testid="ical-inbox-refresh" className="justify-center">
              <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
            </Button>
            {/* iter209 — Importa CSV/XLS Booking direttamente da qui */}
            <label
              htmlFor="ical-xls-upload"
              className="inline-flex items-center justify-center gap-1.5 bg-[#003580] hover:bg-[#00224a] text-white text-xs font-semibold px-3 py-2 rounded-lg cursor-pointer transition-colors"
              title="Carica il file .xls/.csv scaricato da Booking.com: il sistema abbina automaticamente gli 'n?' e completa i dati"
              data-testid="ical-import-xls-trigger"
            >
              {uploadingXls ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span className="sm:hidden">Importa XLS</span>
              <span className="hidden sm:inline">{uploadingXls ? "Importo…" : "Importa XLS/CSV Booking"}</span>
              <input
                id="ical-xls-upload"
                type="file"
                accept=".xls,.xlsx,.csv"
                onChange={askUploadMode}
                className="hidden"
                disabled={uploadingXls}
                data-testid="ical-import-xls-input"
              />
            </label>
            {/* iter212 — Annulla ultimo import CSV */}
            <button
              type="button"
              onClick={openUndoPreview}
              disabled={undoLoading}
              className="inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
              title="Annulla l'ultimo import CSV: cancella le prenotazioni auto-create e riporta gli n? sul calendario"
              data-testid="ical-undo-last-csv-trigger"
            >
              {undoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
              <span className="sm:hidden">Annulla</span>
              <span className="hidden sm:inline">Annulla ultimo import</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-[#635F59] leading-relaxed">
          Le prenotazioni <b>Booking.com</b> arrivano qui via iCal senza nome ospite né codice reale.
          La camera è già bloccata sul calendario. Puoi:
        </p>
        <ul className="text-sm text-[#635F59] list-disc pl-5 mt-1 space-y-0.5">
          <li><b>Importa XLS/CSV Booking</b> (in alto): scarica il file da Booking Extranet e caricalo qui — il sistema riconosce automaticamente gli &quot;n?&quot; via camera + date + cognome, completa i dati mancanti e conferma la prenotazione</li>
          <li><b>Vai su Booking</b>: apre l&apos;extranet nella nuova scheda per scaricare il CSV</li>
          <li><b>Crea manualmente</b>: se non vuoi aspettare il CSV, inserisci nome/telefono/prezzo a mano</li>
          <li><b>Depenna</b>: nascondi la notifica quando hai già gestito la prenotazione</li>
        </ul>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#AC5D45]" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-8 text-center" data-testid="ical-inbox-empty">
          <Inbox className="w-10 h-10 text-[#B8B0A4] mx-auto mb-3" />
          <p className="text-[#635F59]">Nessuna notifica iCal in attesa.</p>
          <p className="text-xs text-[#B8B0A4] mt-1">Le nuove prenotazioni Booking.com appariranno qui automaticamente.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="ical-inbox-list">
          {items.map((it) => (
            <div
              key={it.uid}
              ref={(el) => { rowRefs.current[it.uid] = el; }}
              className={`bg-white border rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 transition-all duration-500 ${
                highlightedUid === it.uid
                  ? "border-[#AC5D45] shadow-lg ring-2 ring-[#AC5D45]/40 bg-[#FFF8F0]"
                  : "border-[#E8E2D9]"
              }`}
              data-testid={`ical-inbox-item-${it.uid}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold px-2 py-0.5 rounded uppercase">
                    {it.source}
                  </span>
                  <span className="font-semibold text-[#1E3A5F] truncate">
                    {it.room_name || "Camera"}
                  </span>
                  {/* iter228 — timestamp arrivo notifica nel titolo,
                      così l'host distingue subito una notifica arrivata
                      "5 min fa" da una che era già in Inbox da giorni. */}
                  {it.first_seen_at && (
                    <span
                      className="text-[10px] text-[#8B8377] italic ml-auto"
                      title={new Date(it.first_seen_at).toLocaleString("it-IT")}
                    >
                      Arrivata {fmtRelativeShort(it.first_seen_at)}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2 text-sm text-[#635F59] flex-wrap">
                  <CalendarDays className="w-4 h-4 text-[#B8B0A4]" />
                  <span>{fmtDate(it.check_in)} → {fmtDate(it.check_out)}</span>
                  <span className="text-[#B8B0A4]">·</span>
                  <span>{it.nights}n</span>
                  {it.price_per_night ? (
                    <>
                      <span className="text-[#B8B0A4]">·</span>
                      <span className="italic">stima €{(it.price_per_night * it.nights).toFixed(0)}</span>
                    </>
                  ) : null}
                </div>
                {it.summary && (
                  <div className="mt-1 text-xs text-[#B8B0A4] truncate italic">{it.summary}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 bg-[#003580] hover:bg-[#00224a] text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  data-testid={`ical-inbox-go-booking-${it.uid}`}
                >
                  Vai su Booking <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => onCreateBookingFromIcal && onCreateBookingFromIcal({
                    room_id: it.room_id,
                    room_name: it.room_name,
                    check_in: it.check_in,
                    check_out: it.check_out,
                    source_hint: "manual_from_ical",
                    ical_uid: it.uid,
                  })}
                  data-testid={`ical-inbox-create-${it.uid}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Crea manualmente
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => dismiss(it.uid)}
                  disabled={busyUid === it.uid}
                  data-testid={`ical-inbox-dismiss-${it.uid}`}
                  title="Depenna: nasconde dalla lista, la camera resta occupata sul calendario"
                >
                  {busyUid === it.uid ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* iter210 — Dialog: come gestire righe XLS non-abbinate agli n? */}
      {pendingFile && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPendingFile(null)}
          data-testid="ical-import-mode-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="font-display text-xl text-[#1E3A5F]">Come gestire le righe non abbinate?</h3>
            <p className="text-sm text-[#635F59] leading-relaxed">
              Sto per importare <b>{pendingFile.name}</b>. Il sistema abbinerà automaticamente le righe con gli
              &quot;n?&quot; già presenti (camera + date + cognome). Alcune righe potrebbero <b>non trovare corrispondenza</b>
              (es. prenotazioni nuovissime non ancora arrivate via iCal). Cosa fai?
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => doDryRun(false)}
                className="w-full text-left bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg p-3 transition"
                data-testid="ical-import-mode-create-new"
              >
                <div className="font-semibold text-emerald-900">Crea automaticamente le nuove</div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Le righe senza corrispondente iCal diventano prenotazioni confermate a tutti gli effetti.
                </div>
              </button>
              <button
                type="button"
                onClick={() => doDryRun(true)}
                className="w-full text-left bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-lg p-3 transition"
                data-testid="ical-import-mode-only-merge"
              >
                <div className="font-semibold text-amber-900">Solo abbina agli n? — le nuove le gestisco a mano</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Aggiorno solo gli n? già in inbox. Le righe non abbinate verranno elencate a fine import così puoi decidere caso per caso.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setPendingFile(null)}
                className="w-full text-center text-xs text-stone-500 hover:text-stone-700 py-2"
                data-testid="ical-import-mode-cancel"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter211 — Modal anteprima: lista match con checkbox per riga */}
      {preview && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4"
          data-testid="ical-import-preview-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E8E2D9]">
              <h3 className="font-display text-xl text-[#1E3A5F]">Anteprima modifiche · approva riga per riga</h3>
              <p className="text-xs text-[#635F59] mt-1">
                Spunta solo le righe che vuoi applicare. Le altre restano invariate (gli n? attuali sul calendario non vengono toccati).
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-2">
              {preview.matches.map((m) => {
                const checked = preview.selected.has(m.code);
                const isUpdate = m.match_type === "update";
                return (
                  <label
                    key={m.code}
                    className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      checked ? (isUpdate ? "bg-emerald-50 border-emerald-300" : "bg-blue-50 border-blue-300")
                              : "bg-stone-50 border-stone-200 opacity-60"
                    }`}
                    data-testid={`ical-preview-row-${m.code}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = new Set(preview.selected);
                        if (e.target.checked) next.add(m.code); else next.delete(m.code);
                        setPreview({ ...preview, selected: next });
                      }}
                      className="mt-1 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-semibold text-[#1E3A5F]">{m.guest_name}</div>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                          isUpdate ? "bg-emerald-600 text-white" : "bg-blue-600 text-white"
                        }`}>
                          {isUpdate ? "AGGIORNA n?" : "NUOVA"}
                        </span>
                      </div>
                      <div className="text-xs text-[#635F59] mt-0.5">
                        <b>{m.code}</b> · {m.room_name} · {m.arrival} → {m.departure} · {m.nights || "?"} notti · €{Number(m.total || 0).toFixed(2)}
                      </div>
                      {isUpdate && m.old_code && m.old_code !== m.code && (
                        <div className="text-[11px] text-emerald-700 mt-0.5">
                          n? esistente ({m.old_code}) verrà rinominato in <b>{m.code}</b>
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="px-6 py-3 border-t border-[#E8E2D9] flex items-center justify-between gap-2">
              <div className="text-xs text-[#635F59]">
                Selezionate <b>{preview.selected.size}</b> di {preview.matches.length}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setPreview(null); setPendingFile(null); setPendingMode(null); }}
                  className="text-xs text-stone-500 hover:text-stone-700 px-3 py-2"
                  data-testid="ical-preview-cancel"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={applyApproved}
                  disabled={uploadingXls || preview.selected.size === 0}
                  className="text-sm bg-[#AC5D45] hover:bg-[#8A4936] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg"
                  data-testid="ical-preview-apply"
                >
                  {uploadingXls ? "Applico…" : `Applica ${preview.selected.size} selezionate`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* iter212 — Modal conferma annullamento ultimo import CSV */}
      {undoPreview && (
        <div
          className="fixed inset-0 z-[210] bg-black/60 flex items-center justify-center p-4"
          data-testid="ical-undo-preview-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E8E2D9] bg-red-50">
              <h3 className="font-display text-xl text-red-900 flex items-center gap-2">
                <Undo2 className="w-5 h-5" /> Annulla ultimo import CSV
              </h3>
              <p className="text-xs text-red-800 mt-1">
                Import del <b>{new Date(undoPreview.imported_at).toLocaleString("it-IT")}</b>
                {" · "}Verranno cancellate <b>{undoPreview.will_delete?.length || 0}</b> prenotazioni auto-create
                {undoPreview.will_unmerge_externals > 0 && (
                  <> e ripristinati <b>{undoPreview.will_unmerge_externals}</b> iCal &quot;n?&quot; sul calendario</>
                )}.
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {undoPreview.will_delete?.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-red-700 uppercase mb-2">
                    Da cancellare ({undoPreview.will_delete.length})
                  </div>
                  <div className="space-y-1">
                    {undoPreview.will_delete.map((r) => (
                      <div
                        key={r.code}
                        className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                        data-testid={`ical-undo-delete-row-${r.code}`}
                      >
                        <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <span className="font-mono text-red-700 shrink-0">{r.code}</span>
                        <span className="text-stone-500 shrink-0">·</span>
                        <span className="font-semibold text-[#1E3A5F] truncate">{r.guest_name}</span>
                        <span className="text-stone-500 shrink-0">·</span>
                        <span className="text-[#635F59] shrink-0">{r.room_name || "-"}</span>
                        <span className="text-stone-500 shrink-0">·</span>
                        <span className="text-[#635F59] shrink-0">{fmtDateShort(r.check_in)} → {fmtDateShort(r.check_out)}</span>
                        {r.total ? (
                          <>
                            <span className="text-stone-500 shrink-0 ml-auto">·</span>
                            <span className="text-[#635F59] shrink-0">€{Number(r.total).toFixed(2)}</span>
                          </>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {undoPreview.will_keep_updated?.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-amber-700 uppercase mb-2">
                    Aggiornamenti su prenotazioni pre-esistenti · NON verranno toccati ({undoPreview.will_keep_updated.length})
                  </div>
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                    Queste erano già a DB (es. n? Booking) e sono state solo <b>arricchite</b> con nome/dati.
                    Per riportarle allo stato originale serve lo &quot;Storico completo&quot; (in arrivo).
                  </div>
                  <div className="space-y-1">
                    {undoPreview.will_keep_updated.map((r) => (
                      <div
                        key={r.code}
                        className="flex items-center gap-2 text-xs bg-amber-50/50 border border-amber-200 rounded-lg px-3 py-2 opacity-80"
                      >
                        <span className="font-mono text-amber-700 shrink-0">{r.code}</span>
                        <span className="text-stone-500 shrink-0">·</span>
                        <span className="font-semibold text-[#1E3A5F] truncate">{r.guest_name}</span>
                        <span className="text-stone-500 shrink-0">·</span>
                        <span className="text-[#635F59] shrink-0">{fmtDateShort(r.check_in)} → {fmtDateShort(r.check_out)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(undoPreview.will_delete?.length || 0) === 0 && (
                <div className="text-center text-sm text-stone-500 py-8">
                  Nessuna prenotazione da cancellare in questo batch.
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-[#E8E2D9] flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setUndoPreview(null)}
                className="text-xs text-stone-500 hover:text-stone-700 px-3 py-2"
                data-testid="ical-undo-cancel"
              >
                Non annullare
              </button>
              <button
                type="button"
                onClick={confirmUndo}
                disabled={undoLoading || (undoPreview.will_delete?.length || 0) === 0}
                className="text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2"
                data-testid="ical-undo-confirm"
              >
                {undoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                Sì, annulla l&apos;import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
